// ============================================================================
// INTELLIGENT ALERTS TYPES
// ============================================================================

export type AlertSeverity = 'info' | 'warning' | 'critical' | 'success';
export type AlertCategory =
  | 'schedule'
  | 'resource'
  | 'budget'
  | 'quality'
  | 'risk'
  | 'dependency'
  | 'performance'
  | 'team';

export type AlertStatus = 'active' | 'acknowledged' | 'resolved' | 'dismissed';

export interface Alert {
  id: string;
  title: string;
  message: string;
  severity: AlertSeverity;
  category: AlertCategory;
  status: AlertStatus;

  // Context
  relatedEntityType?: 'node' | 'edge' | 'risk' | 'member' | 'kanban-card';
  relatedEntityId?: string;

  // Metadata
  detectedAt: Date;
  acknowledgedAt?: Date;
  resolvedAt?: Date;
  acknowledgedBy?: string;

  // Actions
  suggestedActions?: string[];
  automationAvailable?: boolean;

  // Metrics
  impact: number; // 1-10 scale
  urgency: number; // 1-10 scale
  priority: number; // Calculated: impact * urgency
}

export interface AlertRule {
  id: string;
  name: string;
  description: string;
  category: AlertCategory;
  enabled: boolean;

  // Detection logic
  condition: (context: AlertContext) => boolean;
  severity: AlertSeverity;

  // Thresholds
  threshold?: number;
  comparisonOperator?: '>' | '<' | '>=' | '<=' | '==' | '!=';

  // Timing
  checkIntervalMinutes?: number;
  cooldownMinutes?: number; // Prevent duplicate alerts
  lastTriggered?: Date;
}

export interface AlertContext {
  // Project data
  nodes: any[];
  edges: any[];
  members: any[];
  risks: any[];
  kanbanCards?: any[];

  // Computed metrics
  statistics: any;
  criticalPath: string[];

  // Temporal context
  currentDate: Date;
  projectStartDate?: Date;
  projectEndDate?: Date;
}

export interface AlertConfig {
  enabled: boolean;
  autoAcknowledge: boolean;
  dismissAfterDays: number;

  // Notification preferences
  showNotifications: boolean;
  playSound: boolean;

  // Severity filters
  enabledSeverities: {
    info: boolean;
    warning: boolean;
    critical: boolean;
    success: boolean;
  };

  // Category filters
  enabledCategories: {
    schedule: boolean;
    resource: boolean;
    budget: boolean;
    quality: boolean;
    risk: boolean;
    dependency: boolean;
    performance: boolean;
    team: boolean;
  };
}

export interface AlertStatistics {
  totalActive: number;
  totalAcknowledged: number;
  totalResolved: number;
  totalDismissed: number;

  bySeverity: Record<AlertSeverity, number>;
  byCategory: Record<AlertCategory, number>;

  criticalAlerts: Alert[];
  recentAlerts: Alert[];

  avgResolutionTime: number; // in hours
  oldestActiveAlert?: Alert;
}

// Default alert rules
export const DEFAULT_ALERT_RULES: Omit<AlertRule, 'condition'>[] = [
  {
    id: 'critical-path-delay',
    name: 'Retard sur le Chemin Critique',
    description: 'Détecte les tâches du chemin critique en retard',
    category: 'schedule',
    enabled: true,
    severity: 'critical',
  },
  {
    id: 'approaching-deadline',
    name: 'Échéance Proche',
    description: 'Alerte quand une tâche approche de son échéance (< 3 jours)',
    category: 'schedule',
    enabled: true,
    severity: 'warning',
    threshold: 3,
  },
  {
    id: 'overallocated-resource',
    name: 'Ressource Sur-allouée',
    description: 'Détecte les membres assignés à trop de tâches simultanément',
    category: 'resource',
    enabled: true,
    severity: 'warning',
    threshold: 5,
  },
  {
    id: 'high-risk-unmitigated',
    name: 'Risque Élevé Non Traité',
    description: 'Alerte sur les risques critiques sans plan de mitigation',
    category: 'risk',
    enabled: true,
    severity: 'critical',
  },
  {
    id: 'blocked-tasks',
    name: 'Tâches Bloquées',
    description: 'Détecte les tâches bloquées depuis plus de 48h',
    category: 'dependency',
    enabled: true,
    severity: 'warning',
    threshold: 48,
  },
  {
    id: 'low-team-velocity',
    name: 'Vélocité Équipe Faible',
    description: 'Alerte si le taux de complétion des tâches diminue',
    category: 'performance',
    enabled: true,
    severity: 'warning',
    threshold: 30, // percentage
  },
  {
    id: 'wip-limit-exceeded',
    name: 'Limite WIP Dépassée',
    description: 'Détecte le dépassement des limites Work In Progress',
    category: 'performance',
    enabled: true,
    severity: 'warning',
  },
  {
    id: 'unassigned-tasks',
    name: 'Tâches Non Assignées',
    description: 'Alerte sur les tâches critiques sans assignation',
    category: 'team',
    enabled: true,
    severity: 'warning',
  },
];

export const DEFAULT_ALERT_CONFIG: AlertConfig = {
  enabled: true,
  autoAcknowledge: false,
  dismissAfterDays: 7,

  showNotifications: true,
  playSound: false,

  enabledSeverities: {
    info: true,
    warning: true,
    critical: true,
    success: true,
  },

  enabledCategories: {
    schedule: true,
    resource: true,
    budget: true,
    quality: true,
    risk: true,
    dependency: true,
    performance: true,
    team: true,
  },
};

// Alert severity metadata
export const ALERT_SEVERITIES = [
  {
    key: 'info' as AlertSeverity,
    label: 'Information',
    color: '#3b82f6',
    icon: 'info',
  },
  {
    key: 'warning' as AlertSeverity,
    label: 'Avertissement',
    color: '#f59e0b',
    icon: 'alert-triangle',
  },
  {
    key: 'critical' as AlertSeverity,
    label: 'Critique',
    color: '#ef4444',
    icon: 'alert-circle',
  },
  {
    key: 'success' as AlertSeverity,
    label: 'Succès',
    color: '#10b981',
    icon: 'check-circle',
  },
];

// Alert category metadata
export const ALERT_CATEGORIES = [
  {
    key: 'schedule' as AlertCategory,
    label: 'Planning',
    icon: 'calendar',
    description: 'Alertes liées aux échéances et au calendrier',
  },
  {
    key: 'resource' as AlertCategory,
    label: 'Ressources',
    icon: 'users',
    description: 'Alertes sur l\'allocation des ressources',
  },
  {
    key: 'budget' as AlertCategory,
    label: 'Budget',
    icon: 'dollar-sign',
    description: 'Alertes financières et budgétaires',
  },
  {
    key: 'quality' as AlertCategory,
    label: 'Qualité',
    icon: 'check-circle',
    description: 'Alertes sur la qualité des livrables',
  },
  {
    key: 'risk' as AlertCategory,
    label: 'Risques',
    icon: 'alert-triangle',
    description: 'Alertes liées aux risques projet',
  },
  {
    key: 'dependency' as AlertCategory,
    label: 'Dépendances',
    icon: 'link',
    description: 'Alertes sur les dépendances entre tâches',
  },
  {
    key: 'performance' as AlertCategory,
    label: 'Performance',
    icon: 'trending-up',
    description: 'Alertes sur la performance de l\'équipe',
  },
  {
    key: 'team' as AlertCategory,
    label: 'Équipe',
    icon: 'users',
    description: 'Alertes concernant l\'équipe',
  },
];
